import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import { Check, Timer, Star, TrendingUp, Wallet, Send } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testId, tone = "zinc" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    zinc: "bg-zinc-100 text-zinc-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="card-surface p-4" data-testid={testId}>
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${tones[tone]}`}><Icon className="h-4 w-4" /></div>
      <div className="mt-2 label-caps text-[10px]">{label}</div>
      <div className="text-xl font-bold text-zinc-900 mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function MerchantAnalytics() {
  const { t, tCat } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/merchant/analytics").then(r => setData(r.data)).catch(e => setErr(errMsg(e)));
  }, []);

  if (err) return <div className="app-shell p-5 text-red-600">{err}</div>;
  if (!data) return <div className="app-shell p-5">…</div>;

  const maxCount = Math.max(1, ...data.by_category.map(c => c.count));

  return (
    <div className="app-shell pb-24" data-testid="merchant-analytics">
      <TopBar title={t("analytics")} />
      <div className="px-5 pt-4">
        <h1 className="font-display text-2xl font-bold">{t("yourPerformance")}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t("insightsSub")}</p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <Stat icon={TrendingUp} label={t("acceptanceRate")} value={`${data.acceptance_rate}%`} sub={`${data.accepted}/${data.accepted + data.rejected || 1} ${t("decided")}`} tone="emerald" testId="analytics-acceptance" />
          <Stat icon={Timer} label={t("avgResponse")} value={data.avg_response_minutes != null ? `${data.avg_response_minutes} ${t("minutes")}` : "—"} sub={t("requestToOffer")} testId="analytics-response" />
          <Stat icon={Send} label={t("totalOffers")} value={data.total_offers} sub={`${data.pending} ${t("pending")}`} testId="analytics-total-offers" />
          <Stat icon={Check} label={t("completedOrders")} value={data.completed_orders} testId="analytics-completed" tone="emerald" />
          <Stat icon={Wallet} label={t("revenueSar")} value={data.revenue.toFixed(0)} sub={`${t("commissionPaid")} ${data.commission_paid.toFixed(0)}`} testId="analytics-revenue" />
          <Stat icon={Star} label={t("ratingLabel")} value={data.rating != null ? data.rating.toFixed(1) : "—"} sub={`${data.rating_count} ${t("reviews")}`} tone="amber" testId="analytics-rating" />
        </div>

        <div className="mt-6">
          <p className="label-caps mb-3">{t("avgPriceByCat")}</p>
          {data.by_category.length === 0 && (
            <div className="card-surface p-5 text-sm text-zinc-600" data-testid="cat-empty">{t("noAnalyticsData")}</div>
          )}
          <div className="space-y-3">
            {data.by_category.map((c) => (
              <div key={c.category} className="card-surface p-4" data-testid={`cat-row-${c.category}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{tCat(c.category)}</div>
                  <div className="text-sm text-emerald-700 font-bold">{c.avg_price.toFixed(0)} {t("saudiRiyal")}</div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{c.count} {t("offers")}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="card-surface p-4" data-testid="card-accepted">
            <div className="label-caps text-[10px] text-emerald-700">{t("accepted")}</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{data.accepted}</div>
          </div>
          <div className="card-surface p-4" data-testid="card-rejected">
            <div className="label-caps text-[10px] text-zinc-500">{t("rejected")}</div>
            <div className="text-2xl font-bold text-zinc-700 mt-1">{data.rejected}</div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
